// @ts-expect-error moduleResolution:nodenext issue 54523
import { html, css } from 'lit';
// @ts-expect-error moduleResolution:nodenext issue 54523
import { customElement, property, state } from 'lit/decorators.js';
// @ts-expect-error moduleResolution:nodenext issue 54523
import { classMap } from 'lit/directives/class-map.js';
import LightElement from './light-element.js';
import { IProductVariant, IVariantOptionGroup } from '../../typescript/models/product-variant-types.js';

export interface VariantSelectorElement extends HTMLElement {
	variants: IProductVariant[];
	variantOptionGroups: IVariantOptionGroup[];
	isValid: (showErrors?: boolean) => boolean;
	showValidationErrorsForMissingOptions: () => void;
}

@customElement('product-variant-selector')
export default class ProductVariantSelector extends LightElement {

	@property({ type: Array })
	variants: IProductVariant[] = [];

	@property({ type: Array })
	variantOptionGroups: IVariantOptionGroup[] = [];

	@property({ type: Object })
	selectedOptions: { [key: string]: string } = {};

	@state()
	private currentVariant: IProductVariant | null = null;

	@state()
	private openDropdownKey: string | null = null;

	@state()
	private showValidationErrors: boolean = false;

	@state()
	private focusedOptionIndex: number = -1;

	@state()
	private isMobile: boolean = false;

	private _boundCloseDropdowns = this.closeDropdowns.bind(this);
	private _boundHandleKeyDown = this.handleKeyDown.bind(this) as (e: KeyboardEvent) => void;
	private _boundHandleDelegatedClick = this.handleDelegatedClick.bind(this);

	public isValid(showErrors: boolean = true): boolean {
		if (showErrors) {
			this.showValidationErrors = true;
			this.requestUpdate();
		}
		return this.currentVariant !== null;
	}

	public showValidationErrorsForMissingOptions(): void {
		this.showValidationErrors = true;
		this.requestUpdate();
	}

	public get selectedVariant(): IProductVariant | null {
		return this.currentVariant;
	}

	connectedCallback() {
		super.connectedCallback();
		// clear out any placeholder html inside the variant selector
		this.innerHTML = '';
		this.isMobile = window.matchMedia("(max-width: 979px)").matches;
		this.updateCurrentVariant();
		this.checkIfHasComeFromWishlist();
		document.addEventListener('click', this._boundCloseDropdowns);
		document.addEventListener('keydown', this._boundHandleKeyDown);
		this.addEventListener('click', this._boundHandleDelegatedClick);
		this.setupKeyAttributesLinkHandler();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('click', this._boundCloseDropdowns);
		document.removeEventListener('keydown', this._boundHandleKeyDown);
		this.removeEventListener('click', this._boundHandleDelegatedClick);
	}

	updated(changedProperties: Map<string | number | symbol, unknown>) {
		if (changedProperties.has('variants') || changedProperties.has('variantOptionGroups')) {
			if (changedProperties.has('variantOptionGroups') && this.variantOptionGroups.length > 0) {
				this.initializeSelectedOptionsFromVariantOptions();
			}
			this.updateCurrentVariant();
		}
	}

	private checkIfHasComeFromWishlist() {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('wishlist') && urlParams.get('wishlist') === 'true') {
			this.isValid(true);
		}
	}

	private initializeSelectedOptionsFromVariantOptions() {
		if (Object.keys(this.selectedOptions).length === 0) {
			const initialSelection: { [key: string]: string } = {};

			this.variantOptionGroups.forEach(optionGroup => {
				const selectedOption = optionGroup.Options.find(option => option.Selected);
				if (selectedOption) {
					initialSelection[optionGroup.Name] = selectedOption.Value;
				}
			});

			if (Object.keys(initialSelection).length > 0) {
				this.selectedOptions = initialSelection;
			}
		}
	}

	private updateCurrentVariant() {
		const selectedKeys = Object.keys(this.selectedOptions);
		const totalKeys = this.variantOptionGroups.length;

		if (selectedKeys.length === totalKeys) {
			// Find exact match
			this.currentVariant = this.variants.find(variant =>
				this.variantOptionGroups.every(optionGroup => {
					const key = optionGroup.Name;
					return variant.VariantKeyValues[key] === this.selectedOptions[key];
				})
			) || null;
		} else {
			this.currentVariant = null;
		}

		// Clear validation errors if we have a valid selection
		if (this.currentVariant !== null) {
			this.showValidationErrors = false;
		}

		// Dispatch event when selection changes
		this.dispatchEvent(new CustomEvent('variant-changed', {
			detail: {
				selectedOptions: this.selectedOptions,
				currentVariant: this.currentVariant
			},
			bubbles: true
		}));
	}

	private isOptionAvailable(variantKey: string, optionValue: string): boolean {
		// If no options are selected yet, all are available
		if (Object.keys(this.selectedOptions).length === 0) {
			return true;
		}

		// Check if there's any variant that matches the current selection plus this option
		const testSelection = { ...this.selectedOptions, [variantKey]: optionValue };

		return this.variants.some(variant => {
			return Object.keys(testSelection).every(key =>
				variant.VariantKeyValues[key] === testSelection[key]
			);
		});
	}

	private handleOptionClick(variantKey: string, optionValue: string) {
		// Close any open dropdown when an option is selected
		this.openDropdownKey = null;

		// Handle deselection case (empty value means "Select an option" was clicked)
		if (optionValue === '') {
			const newSelection = { ...this.selectedOptions };
			delete newSelection[variantKey];
			this.selectedOptions = newSelection;
			this.updateCurrentVariant();
			return;
		}

		const isCurrentlySelected = this.selectedOptions[variantKey] === optionValue;
		const isAvailable = this.isOptionAvailable(variantKey, optionValue);

		if (isCurrentlySelected) {
			// Deselect current option
			const newSelection = { ...this.selectedOptions };
			delete newSelection[variantKey];
			this.selectedOptions = newSelection;
		} else if (isAvailable) {
			// Select new option
			this.selectedOptions = { ...this.selectedOptions, [variantKey]: optionValue };
		} else {

			// Start with just the new selection
			const newSelection = { [variantKey]: optionValue };

			// For each existing selection, check if it's compatible with the new choice
			// by building up the selection incrementally and testing at each step
			for (const [key, value] of Object.entries(this.selectedOptions)) {
				if (key !== variantKey) {
					// Test if we can add this existing selection to our new selection
					const testSelection = { ...newSelection, [key]: value };

					// Check if this combination exists in any variant
					const isCompatible = this.variants.some(variant => {
						const matches = Object.keys(testSelection).every(testKey =>
							variant.VariantKeyValues[testKey] === testSelection[testKey]
						);
						return matches;
					});

					if (isCompatible) {
						newSelection[key] = value;
					}

				}
			}

			this.selectedOptions = newSelection;
		}

		this.updateCurrentVariant();
	}

	private handleDelegatedClick(event: Event) {
		const target = event.target as HTMLElement;

		// Handle variant option clicks (buttons, swatches, and dropdown options)
		const optionButton = target.closest('[data-variant-key][data-option-value]') as HTMLElement;
		if (optionButton) {
			const variantKey = optionButton.dataset.variantKey!;
			const optionValue = optionButton.dataset.optionValue!;
			optionButton.blur();
			this.handleOptionClick(variantKey, optionValue);
			return;
		}

		// Handle dropdown header clicks
		const dropdownHeader = target.closest('[data-dropdown-key]') as HTMLElement;
		if (dropdownHeader) {
			const variantKey = dropdownHeader.dataset.dropdownKey!;
			this.toggleDropdown(variantKey, event);
			return;
		}
	}

	private isOptionGroupValid(optionGroupName: string): boolean {
		return this.selectedOptions.hasOwnProperty(optionGroupName) &&
			this.selectedOptions[optionGroupName] !== '';
	}

	private shouldShowErrorForGroup(optionGroup: IVariantOptionGroup): boolean {
		return this.showValidationErrors && !this.isOptionGroupValid(optionGroup.Name);
	}

	private setupKeyAttributesLinkHandler() {
		const links = document.querySelectorAll('.js-show-variant-errors');
		links.forEach(link => {
			link.addEventListener('click', () => {
				this.showValidationErrorsForMissingOptions();
			});
		});
	}

	private toggleDropdown(variantKey: string, event: Event) {
		event.stopPropagation();
		const wasOpen = this.openDropdownKey === variantKey;
		this.openDropdownKey = wasOpen ? null : variantKey;

		// Reset focused option when opening/closing dropdown
		if (!wasOpen) {
			// When opening, focus on currently selected option or first available
			const optionGroup = this.variantOptionGroups.find(group => group.Name === variantKey);
			if (optionGroup) {
				const selectedOptionIndex = optionGroup.Options.findIndex(opt =>
					this.selectedOptions[variantKey] === opt.Value
				);
				this.focusedOptionIndex = selectedOptionIndex >= 0 ? selectedOptionIndex : -1;
			}
		} else {
			this.focusedOptionIndex = -1;
		}
	}

	private closeDropdowns() {
		if (this.openDropdownKey === null && this.focusedOptionIndex === -1) {
			return;
		}
		this.openDropdownKey = null;
		this.focusedOptionIndex = -1;
	}

	private handleKeyDown(event: KeyboardEvent) {
		// Only handle global events when a dropdown is already open
		if (this.openDropdownKey !== null) {
			const currentGroup = this.variantOptionGroups.find(group => group.Name === this.openDropdownKey);
			if (!currentGroup) return;

			switch (event.key) {
				case 'Escape':
					this.closeDropdowns();
					break;
				case 'ArrowDown':
					event.preventDefault();
					this.navigateOptions(currentGroup, 1);
					break;
				case 'ArrowUp':
					event.preventDefault();
					this.navigateOptions(currentGroup, -1);
					break;
				case 'Enter':
				case ' ': // Space key
					event.preventDefault();
					if (this.focusedOptionIndex >= 0 && this.focusedOptionIndex < currentGroup.Options.length) {
						const option = currentGroup.Options[this.focusedOptionIndex];
						this.handleOptionClick(currentGroup.Name, option.Value);
					}
					break;
			}
		} else if (event.key === 'Escape') {
			this.closeDropdowns();
		}
	}

	private navigateOptions(optionGroup: IVariantOptionGroup, direction: number) {
		const availableOptions = optionGroup.Options.filter(option =>
			this.isOptionAvailable(optionGroup.Name, option.Value)
		);

		if (availableOptions.length === 0) return;

		// Map available options to their original indices
		const availableIndices = availableOptions.map(option =>
			optionGroup.Options.findIndex(opt => opt.Value === option.Value)
		);

		let currentAvailableIndex = availableIndices.indexOf(this.focusedOptionIndex);

		if (currentAvailableIndex === -1) {
			// No current focus, start from beginning or end based on direction
			currentAvailableIndex = direction > 0 ? 0 : availableIndices.length - 1;
		} else {
			// Move to next/previous available option
			currentAvailableIndex = (currentAvailableIndex + direction + availableIndices.length) % availableIndices.length;
		}

		this.focusedOptionIndex = availableIndices[currentAvailableIndex];

		// Scroll the focused option into view after the DOM updates
		this.updateComplete.then(() => {
			this.scrollFocusedOptionIntoView(optionGroup.Name);
		});
	}

	private scrollFocusedOptionIntoView(optionGroupName: string) {
		if (this.focusedOptionIndex === -1) return;

		// Find the dropdown container and the focused option
		const dropdownContainer = this.shadowRoot?.querySelector(`#dropdown-${optionGroupName}`) ||
			this.querySelector(`#dropdown-${optionGroupName}`);

		if (!dropdownContainer) return;

		const focusedOption = dropdownContainer.querySelector(`.c-product-variant-selector__dropdown-option.focused`);

		if (focusedOption && dropdownContainer) {
			focusedOption.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'nearest'
			});
		}
	}

	private renderVariantGroup(optionGroup: IVariantOptionGroup,) {
		const showError = this.shouldShowErrorForGroup(optionGroup);
		const selectedOption = optionGroup.Options.find(opt => this.selectedOptions[optionGroup.Name] === opt.Value);
		const labelText = selectedOption && optionGroup.DisplayVariantOptionSwatches ? `${optionGroup.DisplayName}: ${selectedOption.FormattedValue}` : `${optionGroup.DisplayName}:`;

		return html`
			<div class="c-product-variant-selector__group" data-test="product-variant-selector-group">
				<div class="c-product-variant-selector__label" data-test="product-variant-selector-group-label">${labelText}</div>
				<div class="c-product-variant-selector__options">
					${this.renderVariantOptions(optionGroup)}
				</div>
				${showError ? html`
					<span class="c-product-variant-selector__error field-validation-error"
						  data-test="product-variant-selector-error"
							role="alert"
						  data-valmsg-for="${optionGroup.Name}"
						  data-valmsg-replace="false">
							Please select an option <span class="u-visuallyhidden">for ${optionGroup.DisplayName}</span>
					</span>
				` : ''}
			</div>
		`;
	}

	private renderVariantOptions(optionGroup: IVariantOptionGroup) {
		if (optionGroup.DisplayVariantOptionDropdown) {
			return this.renderDropdown(optionGroup);
		} else if (optionGroup.DisplayVariantOptionSwatches) {
			return this.renderSwatches(optionGroup);
		} else {
			return this.isMobile ? this.renderDropdown(optionGroup) : this.renderButtons(optionGroup);
		}
	}

	private renderButtons(optionGroup: IVariantOptionGroup) {
		return optionGroup.Options.map(option => {
			const isSelected = this.selectedOptions[optionGroup.Name] === option.Value;
			const isAvailable = this.isOptionAvailable(optionGroup.Name, option.Value);

			const classes = {
				'c-button c-button--secondary c-product-variant-selector__button': true,
				'selected': isSelected,
				'disabled': !isAvailable && !isSelected
			};

			return html`
				<button
					class=${classMap(classes)}
					data-variant-key="${optionGroup.Name}"
					data-option-value="${option.Value}"
					type="button"
					data-test="${`product-variant-selector-option-button`}"
				>
					${option.FormattedValue}
				</button>
			`;
		});
	}

	private renderSwatches(optionGroup: IVariantOptionGroup) {
		return optionGroup.Options.map(option => {
			const isSelected = this.selectedOptions[optionGroup.Name] === option.Value;
			const isAvailable = this.isOptionAvailable(optionGroup.Name, option.Value);

			const classes = {
				'c-product-variant-selector__swatch': true,
				'selected': isSelected,
				'disabled': !isAvailable && !isSelected
			};

			return html`
				<button
					class=${classMap(classes)}
					data-variant-key="${optionGroup.Name}"
					data-option-value="${option.Value}"
					type="button"
					data-test="${`product-variant-selector-option-swatch`}"
				>
					${option.HasProductPageColourSwatch ? html`
						<img src="${option.ProductPageColourSwatch}?height=55&width=55&mode=crop&scale=both&format=webp&quality=80" alt="${option.FormattedValue}" class="c-product-variant-selector__swatch-image" width="55" height="55">
					` : html`
						<span class="c-product-variant-selector__swatch-text">${option.FormattedValue}</span>
					`}
				</button>
			`;
		});
	}

	private renderDropdown(optionGroup: IVariantOptionGroup) {
		const selectedOption = optionGroup.Options.find(opt => this.selectedOptions[optionGroup.Name] === opt.Value);
		const isOpen = this.openDropdownKey === optionGroup.Name;
		const dropdownLabel = selectedOption ? selectedOption.FormattedValue : `Select an option`;

		return html`
			<div class="c-product-variant-selector__custom-dropdown">
				<button type="button"
					class="c-product-variant-selector__dropdown-header c-select c-select--large c-select--full"
					data-dropdown-key="${optionGroup.Name}"
					role="combobox"
					aria-controls="dropdown-${optionGroup.Name}"
     			aria-haspopup="listbox"
					aria-expanded="${isOpen ? 'true' : 'false'}"
					data-test="product-variant-selector-dropdown"
				>
					${dropdownLabel}
				</button>
				<div class="c-product-variant-selector__dropdown-options ${isOpen ? 'open' : ''}" id="dropdown-${optionGroup.Name}">
					<button type="button"
						class="c-product-variant-selector__dropdown-option c-product-variant-selector__dropdown-option--deselect"
						data-variant-key="${optionGroup.Name}"
						data-option-value=""
						aria-selected="false"
						data-test="product-variant-selector-option-dropdown-item"
					>
						Select an option
					</button>
					${optionGroup.Options.map((option, index) => {
							const isSelected = this.selectedOptions[optionGroup.Name] === option.Value;
							const isAvailable = this.isOptionAvailable(optionGroup.Name, option.Value);
							const isFocused = this.focusedOptionIndex === index && isOpen;
							const classes = {
								'c-product-variant-selector__dropdown-option': true,
								'selected': isSelected,
								'disabled': !isAvailable && !isSelected,
								'focused': isFocused
							};
							return html`
								<button type="button"
									class=${classMap(classes)}
									data-variant-key="${optionGroup.Name}"
									data-option-value="${option.Value}"
									aria-selected="${isSelected ? 'true' : 'false'}"
									data-test="product-variant-selector-option-dropdown-item"
								>
									${option.FormattedValue}
								</button>
							`;
						})}
				</div>
			</div>
		`;
	}

	render() {
		if (!this.variantOptionGroups || this.variantOptionGroups.length === 0) {
			return html`<p class="c-product-variant-selector__no-options">No options available</p>`;
		}

		return html`
			<div class="c-product-variant-selector__selector">
				${this.variantOptionGroups.map(optionGroup => this.renderVariantGroup(optionGroup))}
			</div>
		`;
	}
}
